import React, {useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {light, regular} from '@fortawesome/fontawesome-svg-core/import.macro';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import {
    Btn, Card,
    Circle,
    ClickableDiv, ErrorText,
    FlexColumnSection,
    FlexDiv,
    FlexRowSection,
    FlexSection,
    GreyText,
    SmallText,
    SpanBold,
} from '../../../styles/globalStyles';
import CustomDatePicker from '../../../components/UI/CustomDatePicker';
import CustomTimePicker from '../../../components/UI/CustomTimePicker';
import UserAvatar from '../../../components/UserAvatar';
import useScreenSize from '../../../helpers/hooks/useScreenSize';
import {isRTL} from '../../../helpers/i18n';
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {useDispatch, useSelector} from "react-redux";
import {DB_DATE_FORMAT, DB_TIME_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import dayjs from "dayjs";
import {Skeleton} from "antd";
import {APPOINTMENT_WAITLIST_SETTINGS, AVAILABILITY_REVEAL_TIME} from "../../../../../src/constants";
import {sortAlphabetically} from "@arboxappv4/shared/src/helpers/functions";
import HowItWorks from "./HowItWorks";
import JoinWaitlistSummary from "./JoinWaitlistSummary";

const JoinAppointmentWaitlist = (props) => {
    const {handleSubmit, values, setFieldValue, errors, touched} = props;
    const box = useSelector(state => state.site.box)
    const flags = useSelector(state => state.stepper.flags)
    const globalLocation = useSelector(state => state.site.selectedLocation)
    const waitlistSetting = useMemo(() => box?.boxes_settings?.find(s => s.prop_name === APPOINTMENT_WAITLIST_SETTINGS)?.properties, [box]);
    const availabilityReveal = useMemo(() => box?.boxes_settings?.find(s => s.prop_name === AVAILABILITY_REVEAL_TIME)?.properties, [box]);
    const maxDate = useMemo(() => {
        if (!availabilityReveal?.revealWeeks) {
            return dayjs().add(30, 'day').format(DB_DATE_FORMAT)
        }
        const {revealDay = 0, revealWeeks} = availabilityReveal
        const today = dayjs().startOf('day')
        const reveal = today.day(revealDay)
        const lastReveal = reveal.isAfter(today, 'day') ? reveal.subtract(7, 'day') : reveal
        return lastReveal.add(revealWeeks * 7 - 1, 'day').format(DB_DATE_FORMAT)
    }, [availabilityReveal]);
    const boxBusinessHours = useMemo(() => waitlistSetting?.business_hours ?? null, [waitlistSetting]);
    const {isMobile} = useScreenSize();

    const [dateMode, setDateMode] = useState('specific');
    const [coaches, setCoaches] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if(box && values?.box_category_id && !coaches) {
            fetchCoaches()
        }
    }, [box, values?.box_category_id]);

    const fetchCoaches = async () => {
        setLoading(true)
        let response = await apiAction(`availabilities/getAvailabilityCoaches`, 'post', {box_categories_id: values?.box_category_id, locations_box_id: globalLocation.id});
        if(response?.length) {
            response = sortAlphabetically(response, 'full_name')
            if (response?.length === 1) {
                setFieldValue('coach_ub_id', response[0]?.id ?? null)
            } else if (flags.coach_ub_id) {
                const selectedIndex = response.findIndex(coach => coach.id === flags.coach_ub_id)
                if (selectedIndex > 0) {
                    const [selectedCoach] = response.splice(selectedIndex, 1)
                    response.unshift(selectedCoach)
                }
            }
            setCoaches(response)
        }
        setLoading(false)
    }

    const onTimeModeSelect = (mode) => {
        if(mode === 'any') {
            if(values.timeRange !== null) {
                setFieldValue('timeRange', null)
            }
        } else {
            if(values.timeRange === null) {
                setFieldValue('timeRange', boxBusinessHours ?? [{from: '09:00:00', to: '14:00:00'}, {from: '16:00:00', to: '18:00:00'}])
            }
        }
    }

    const onDateModeChange = (mode) => {
        let from = values.from ?? dayjs().format(DB_DATE_FORMAT)
        let to = from
        if(mode === 'range') {
            to = dayjs(from, DB_DATE_FORMAT).add(1, 'day').format(DB_DATE_FORMAT)
            if(!values.from) {
                setFieldValue('from', from)
            }
        }
        setFieldValue('to', to)
        setTimeout(() => setDateMode(mode),10)
    }

    const onDateChange = (propName, value) => {
        if(dateMode === 'specific') {
            setFieldValue('from', value)
            setFieldValue('to', value)
        } else {
            if(propName === 'from' && value >= values.to) {
                const to = dayjs(value, DB_DATE_FORMAT).add(1, 'day').format(DB_DATE_FORMAT)
                setFieldValue('to', to)
            }
            setFieldValue(propName, value)
        }
    }

    const addDefaultTimeRange = () => {
        const defaultRange = boxBusinessHours ? boxBusinessHours[0] : {from: '09:00:00', to: '12:00:00'}
        setFieldValue('timeRange', [...values.timeRange, defaultRange])
    }

    const removeTimeRange = (index) => {
        let range = [...values.timeRange]
        range.splice(index, 1)
        setFieldValue('timeRange', range)
    }

    const isDateOutOfAllowedRange = (current) => {
        if (!current) return false
        const date = dayjs(current.format(DB_DATE_FORMAT), DB_DATE_FORMAT)
        const today = dayjs().startOf('day')
        const max = dayjs(maxDate, DB_DATE_FORMAT)
        return date.isBefore(today, 'day') || date.isAfter(max, 'day')
    }

    const isToDateDisabled = (current) => {
        if (isDateOutOfAllowedRange(current)) return true
        if (!values.from) return false
        const date = dayjs(current.format(DB_DATE_FORMAT), DB_DATE_FORMAT)
        return date.isBefore(dayjs(values.from, DB_DATE_FORMAT), 'day')
    }

    const timeToMinutes = (time) => {
        const parsed = dayjs(time, DB_TIME_FORMAT)
        return parsed.hour() * 60 + parsed.minute()
    }

    const getDisableTimePerBusinessHourRow = (index)=> () => {
        const businessRange = boxBusinessHours?.[index]
        const isWithinBusinessRange = (hour, minute) => {
            const candidateMinutes = hour * 60 + minute
            if (businessRange) {
                return (
                    candidateMinutes >= timeToMinutes(businessRange.from) &&
                    candidateMinutes <= timeToMinutes(businessRange.to)
                )
            }
            // Extra row: allow times that sit in any business block
            return (boxBusinessHours ?? []).some(range =>
                candidateMinutes >= timeToMinutes(range.from) &&
                candidateMinutes <= timeToMinutes(range.to)
            )
        }
        return {
            disabledHours: () =>
                [...Array(24).keys()].filter(hour =>
                    ![0, 15, 30, 45].some(minute => isWithinBusinessRange(hour, minute))
                ),
            disabledMinutes: (hour) =>
                [0, 15, 30, 45].filter(minute => !isWithinBusinessRange(hour, minute)),
        }
    }

    const getTimeHelperText = () => {
        let hours = null
        if(boxBusinessHours) {
            hours = boxBusinessHours.map(hour => `${dayjs(hour.from, DB_TIME_FORMAT).format(globalLocation.time_format)} - ${dayjs(hour.to, DB_TIME_FORMAT).format(globalLocation.time_format)}`).join(', ')
        }
        return t(`appointment-waitlist-time-desc${values.timeRange === null ? '-all' : '-range'}${hours ? '-hours' : ''}`, {hours})
    }
    
    const sectionLabel = (title, desc) => (
        <FlexColumnSection flex={'unset'} style={{marginBottom: 12}}>
            <SmallText style={{fontWeight: 600}}>{title}</SmallText>
            <GreyText>{desc}</GreyText>
        </FlexColumnSection>
    );

    if(loading) return <Skeleton style={{padding: isMobile ? '15px' : 'unset'}}/>

    return (
        <FlexSection mobile={isMobile} gap={isMobile ? '16px' : '24px'} align={'start'} width={'100%'} style={{overflow: isMobile ? 'hidden auto' : 'hidden', padding: isMobile ? '15px' : 'unset'}}>
            <Card overflow={isMobile ? 'unset' : 'auto'} maxHeight={isMobile ? 'auto' : 'calc(100% - 2px)'} style={{width: 'inherit'}}>
                {!loading && coaches?.length > 1 &&
                <FlexColumnSection flex={'unset'} overflow={'visible'} padding={isMobile ? '16px' : '24px'} style={{borderBottom: `1px solid ${Colors.siteScheduleBorder}`}}>
                    {sectionLabel(t('appointment-waitlist-coach-preference'), t('appointment-waitlist-coach-preference-desc'))}
                    <FlexRowSection flex={'unset'} gap={'12px'} style={{flexWrap: 'wrap', maxHeight: isMobile ? 'unset' : '200px', overflow: 'auto'}}>
                        {coaches?.map(coach => (
                            <Chip key={`coach-${coach.id}`} selected={values.coach_ub_id === coach.id} onClick={() => setFieldValue('coach_ub_id', coach.id)}>
                                <UserAvatar user={coach} nameType={'full'} size={22} fontSize={'11px'} darkMode={values.coach_ub_id === coach.id}/>
                            </Chip>
                        ))}
                        <Chip selected={values.coach_ub_id == null} onClick={() => setFieldValue('coach_ub_id', null)}>{t('appointment-waitlist-any-coach')}</Chip>
                    </FlexRowSection>
                </FlexColumnSection>
                }

                <FlexColumnSection flex={'unset'} overflow={'visible'} padding={isMobile ? '16px' : '24px'} style={{borderBottom: `1px solid ${Colors.siteScheduleBorder}`}}>
                    {sectionLabel(t('date'), t('appointment-waitlist-date-desc'))}
                    <FlexColumnSection gap={'12px'} flex={'unset'} overflow={'visible'}>
                        <FlexRowSection flex={'unset'} gap={'12px'} style={{flexWrap: 'wrap'}}>
                            <Chip selected={dateMode === 'specific'} size={'sm'} onClick={() => onDateModeChange('specific')}>{t('specific-date')}</Chip>
                            <Chip selected={dateMode === 'range'} size={'sm'} onClick={() => onDateModeChange('range')}>{t('date-range')}</Chip>
                        </FlexRowSection>
                        {dateMode === 'specific' ?
                            <CustomDatePicker onDateSelect={(val) => onDateChange('from', val)}
                                              disabledDate={isDateOutOfAllowedRange}
                                              validRange={[dayjs().startOf('day'), dayjs(maxDate, DB_DATE_FORMAT)]}
                                              defaultDate={values.from}
                                              error={(touched.from && errors.from) ? errors.from : null}
                            />
                            :
                            <FlexSection dir={isMobile ? 'column' : 'row'} flex={'unset'} gap={'12px'} style={{width: '100%'}}>
                                <div style={{width: 'inherit'}}>
                                    {isMobile && <DateLabel fontSize={'14px'}>{t('from')}</DateLabel>}
                                    <CustomDatePicker onDateSelect={(val) => onDateChange('from', val)}
                                                      disabledDate={isDateOutOfAllowedRange}
                                                      validRange={[dayjs().startOf('day'), dayjs(maxDate, DB_DATE_FORMAT)]}
                                                      defaultDate={values.from}
                                                      error={(touched.from && errors.from) ? errors.from : null}
                                                      placeholder={t('start-date')}
                                                      style={{width: 'inherit'}}
                                    />
                                </div>
                                {!isMobile && <FontAwesomeIcon icon={light('arrow-right')} color={Colors.greyText} flip={isRTL() ? 'horizontal' : undefined}/>}
                                <div style={{width: 'inherit'}}>
                                    {isMobile && <DateLabel fontSize={'14px'}>{t('to')}</DateLabel>}
                                    <CustomDatePicker onDateSelect={(val) => onDateChange('to', val)}
                                                      disabledDate={isToDateDisabled}
                                                      validRange={[
                                                          dayjs(values.from || undefined, DB_DATE_FORMAT).isValid()
                                                              ? dayjs(values.from, DB_DATE_FORMAT)
                                                              : dayjs().startOf('day'),
                                                          dayjs(maxDate, DB_DATE_FORMAT)
                                                      ]}
                                                      defaultDate={values.to}
                                                      error={(touched.to && errors.to) ? errors.to : null}
                                                      placeholder={t('end-date')}
                                                      style={{width: 'inherit'}}
                                    />
                                </div>
                            </FlexSection>}
                    </FlexColumnSection>
                </FlexColumnSection>

                <FlexColumnSection flex={'unset'} overflow={'visible'} padding={isMobile ? '16px' : '24px'}>
                    {sectionLabel(t('time'), getTimeHelperText())}
                    <FlexColumnSection gap={'12px'} flex={'unset'} overflow={'visible'}>
                        <FlexRowSection flex={'unset'} gap={'12px'} style={{flexWrap: 'wrap'}}>
                            <Chip selected={values.timeRange === null} size={'sm'} onClick={() => onTimeModeSelect('any')}>{t('any-time')}</Chip>
                            <Chip selected={values.timeRange !== null} size={'sm'} onClick={() => onTimeModeSelect('range')}>{t('time-range')}</Chip>
                        </FlexRowSection>
                        {values.timeRange?.length > 0 &&
                        <FlexColumnSection gap={'15px'} flex={'unset'} overflow={'visible'}>
                            {values.timeRange.map((range, index) => (
                                <FlexRowSection key={`time-range-${index}`} gap={'12px'} flex={'unset'}>
                                    <CustomTimePicker defaultTime={range.from}
                                                      onTimeSelect={(val) => setFieldValue(`timeRange[${index}].from`, val)}
                                                      disabledTime={getDisableTimePerBusinessHourRow(index)}
                                                      status={errors.timeRange?.[index]?.from ? 'error' : null}
                                                      style={{width: 120, height: '35px'}}/>
                                    <FontAwesomeIcon icon={light('arrow-right')} color={Colors.greyText} flip={isRTL() ? 'horizontal' : undefined}/>
                                    <CustomTimePicker defaultTime={range.to}
                                                      onTimeSelect={(val) => setFieldValue(`timeRange[${index}].to`, val)}
                                                      disabledTime={getDisableTimePerBusinessHourRow(index)}
                                                      status={errors.timeRange?.[index]?.to ? 'error' : null}
                                                      style={{width: 120}}/>
                                    {index > 0 &&
                                    <TrashBtn onClick={() => removeTimeRange(index)}>
                                        <FontAwesomeIcon icon={light('trash-can')} color={Colors.siteBlack}/>
                                    </TrashBtn>}
                                </FlexRowSection>
                            ))}
                            {values.timeRange?.length < 3 && <AddTimeRangeBtn onClick={addDefaultTimeRange}>{t('appointment-waitlist-add-time-range')}</AddTimeRangeBtn>}
                        </FlexColumnSection>
                        }
                        {errors?.timeRange && (typeof errors.timeRange === 'string') && <ErrorText>{errors.timeRange}</ErrorText>}
                    </FlexColumnSection>
                </FlexColumnSection>
            </Card>

            <FlexColumnSection gap={'16px'} flex={'unset'} overflow={'visible'} style={{width: isMobile ? '100%' : 340}}>
                <JoinWaitlistSummary values={values} errors={errors} onCTAClick={handleSubmit} coaches={coaches}/>
                <HowItWorks/>
            </FlexColumnSection>
        </FlexSection>
    );
};

export default JoinAppointmentWaitlist;

const Chip = styled(ClickableDiv)`
    display: flex;
    align-items: center;
    gap: 8px;
    height: ${({size}) => size === 'sm' ? '30px' : '36px'};
    padding: 0 ${({size}) => size === 'sm' ? '13px' : '15px'};
    border-radius: ${({size}) => size === 'sm' ? '8px' : '10px'};
    border: 1px solid ${({selected}) => selected ? Colors.siteBlack : Colors.siteScheduleBorder};
    background: ${({selected}) => selected ? Colors.siteChipBg : Colors.white};
    font-size: ${({size}) => size === 'sm' ? '14px' : '15px'};
    font-weight: 500;
    color: ${Colors.siteBlack};
    :hover {
        border-color: ${({selected}) => selected ? Colors.siteBlack : Colors.greyText};
    }
`;

const TrashBtn = styled(ClickableDiv)`
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${Colors.siteScheduleBorder};
    border-radius: 6px;
    :hover {
        background-color: ${Colors.inputHoverBg};
    }
`

const AddTimeRangeBtn = styled(ClickableDiv)`
    font-size: 14px;
    font-weight: 600;
    border-radius: 20px;
    border: 1px solid #E0E0E0;
    padding: 3px 15px;
    width: fit-content;
    :hover {
        border-color: ${Colors.greyText};
    }
`

const DateLabel = styled(GreyText)`
    font-weight: 500;
    margin-bottom: 2px;
`